import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { ORAKLE_WEATHER_WEBSITE } from '../../constants/colors';
import { FiShield, FiMail, FiClock, FiCopy, FiRefreshCw, FiMaximize2 } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminAuth.css';

const SecureQRCode = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [token, setToken] = useState('');
    const [email, setEmail] = useState('');
    const [localError, setLocalError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const { getSecureQR, secureQRState, clearError, clearSecureQRData } = useAdminAuth();

    // Extract token and email from URL parameters
    const urlToken = searchParams.get('token');
    const urlEmail = searchParams.get('email');

    const fetchQRData = useCallback(async () => {
        const tokenToUse = urlToken || token;
        const emailToUse = urlEmail || email;

        if (!tokenToUse || !emailToUse) {
            setLocalError("Token ou email manquant");
            return;
        }

        console.log("🔧 SecureQRCode: Fetching QR data with token:", tokenToUse, "email:", emailToUse);
        setLocalError(null);
        setSuccessMessage(null);

        try {
            await getSecureQR(tokenToUse, emailToUse);
            console.log("🔧 SecureQRCode: QR data request sent successfully");
        } catch (err) {
            console.error("🔧 SecureQRCode: Error in fetchQRData:", err);
            const errorMessage = err instanceof Error ? err.message : "Une erreur est survenue";
            setLocalError(errorMessage);
        }
    }, [urlToken, urlEmail, token, email, getSecureQR]);

    // Clear errors when component mounts
    useEffect(() => {
        console.log("🔧 SecureQRCode: Component mounted");
        console.log("🔧 SecureQRCode: Token from URL:", urlToken);
        console.log("🔧 SecureQRCode: Email from URL:", urlEmail);

        clearSecureQRData();
        clearError();
        setLocalError(null);
        setSuccessMessage(null);

        // Auto-populate from URL params if present
        if (urlToken) setToken(urlToken);
        if (urlEmail) setEmail(urlEmail);

        // Auto-fetch QR data if token and email are present
        if (urlToken && urlEmail) {
            console.log("🔧 SecureQRCode: Auto-fetching QR data");
            fetchQRData();
        } else {
            setLocalError("Token ou email manquant dans l'URL");
        }
    }, [urlToken, urlEmail, fetchQRData, clearSecureQRData, clearError]);

    // Handle Redux error changes
    useEffect(() => {
        if (secureQRState.error) {
            console.log("🔧 SecureQRCode: Redux error detected:", secureQRState.error);
            setSuccessMessage(null);
        }
    }, [secureQRState.error]);

    // Handle success
    useEffect(() => {
        if (secureQRState.qrImage && secureQRState.secret && !secureQRState.error && !secureQRState.isLoading) {
            console.log("🔧 SecureQRCode: Success detected - QR code loaded");
            setLocalError(null);
            setSuccessMessage('✅ QR Code récupéré avec succès ! Le QR code sécurisé a été chargé.');
        }
    }, [secureQRState.qrImage, secureQRState.secret, secureQRState.error, secureQRState.isLoading]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        await fetchQRData();
    };

    const handleRefresh = () => {
        console.log("🔧 SecureQRCode: Manual refresh requested");
        fetchQRData();
    };

    const handleCopySecret = () => {
        if (secureQRState.secret) {
            navigator.clipboard.writeText(secureQRState.secret);
            alert('Clé secrète copiée dans le presse-papiers');
        }
    };

    const handleCopyQRData = () => {
        if (secureQRState.qrData) {
            navigator.clipboard.writeText(secureQRState.qrData);
            alert('Données QR copiées dans le presse-papiers');
        }
    };

    const handleBackToLogin = () => {
        navigate('/admin/login');
    };

    return (
        <div className="admin-auth-container">
            <div className="admin-auth-card" style={{ maxWidth: '600px' }}>
                <div className="admin-auth-header">
                    <div className="login-logo mb-3">
                        <a href={ORAKLE_WEATHER_WEBSITE} target="_blank" rel="noopener noreferrer">
                            <img src="https://logo-orakle.s3.eu-west-3.amazonaws.com/logo_fond_bleu+(3).png" alt="Orakle Weather Logo" className="img-circle" />
                        </a>
                    </div>
                    <div className="icon-header mb-4">
                        <FiShield size={48} className="text-white" />
                    </div>
                    <h1>QR Code sécurisé</h1>
                    <p>Accès sécurisé au QR code MFA via token d'authentification</p>
                </div>

                <form onSubmit={handleSubmit} className="admin-auth-form">
                    {successMessage && (
                        <div className="alert alert-success" role="alert" style={{
                            marginBottom: '20px',
                            padding: '15px',
                            backgroundColor: '#d4edda',
                            borderColor: '#c3e6cb',
                            color: '#155724'
                        }}>
                            <strong>{successMessage}</strong>
                        </div>
                    )}

                    {secureQRState.error && (
                        <div className="alert alert-danger" role="alert">
                            <strong>❌ Erreur de récupération</strong><br />
                            {secureQRState.error}
                        </div>
                    )}

                    {localError && (
                        <div className="alert alert-danger" role="alert">
                            <strong>❌ Erreur</strong><br />
                            {localError}
                        </div>
                    )}

                    <div className="mb-3">
                        <label htmlFor="token" className="form-label">
                            <FiShield className="me-2" />
                            Token
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="token"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="Entrez le token"
                            required
                            disabled={secureQRState.isLoading}
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="email" className="form-label">
                            <FiMail className="me-2" />
                            Adresse email
                        </label>
                        <input
                            type="email"
                            className="form-control"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="admin@example.com"
                            required
                            disabled={secureQRState.isLoading}
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary w-100 mb-3"
                        disabled={secureQRState.isLoading}
                    >
                        {secureQRState.isLoading ? 'Génération en cours...' : 'Générer le QR code sécurisé'}
                    </button>

                    {/* Loading State */}
                    {secureQRState.isLoading && (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-2">Récupération du QR code sécurisé...</p>
                        </div>
                    )}

                    {/* No QR Code State */}
                    {!secureQRState.isLoading && !secureQRState.qrImage && !secureQRState.error && !localError && urlToken && urlEmail && (
                        <div className="text-center py-4">
                            <div className="alert alert-warning" role="alert">
                                <strong>QR Code non disponible</strong><br />
                                Aucun QR code trouvé pour ce token et cet email.
                                Vérifiez que le lien est correct ou contactez votre administrateur.
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary mt-3"
                                onClick={handleRefresh}
                                disabled={secureQRState.isLoading}
                            >
                                Réessayer
                            </button>
                        </div>
                    )}

                    {secureQRState.qrImage && secureQRState.secret && (
                        <div className="qr-display mb-3">
                            <div className="alert alert-info">
                                <strong>✅ Accès sécurisé validé</strong><br />
                                Le QR code a été récupéré avec succès via le token d'authentification.
                            </div>
                            <h5>QR Code MFA Sécurisé</h5>
                            <p className="text-muted">
                                Scannez-le avec votre application d'authentification (Google Authenticator, Authy, etc.)
                            </p>
                            {secureQRState.remainingTime && (
                                <p className="text-muted">
                                    <FiClock className="me-2" />
                                    Temps restant: {secureQRState.remainingTime} secondes
                                </p>
                            )}
                            <div className="d-flex justify-content-center">
                                <div style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    padding: '20px',
                                    borderRadius: '12px',
                                    border: '2px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <img
                                        src={secureQRState.qrImage.startsWith('data:')
                                            ? secureQRState.qrImage
                                            : `data:image/png;base64,${secureQRState.qrImage}`}
                                        alt="QR Code sécurisé"
                                        className="img-fluid"
                                        style={{ maxWidth: '300px' }}
                                    />
                                </div>
                            </div>
                            {secureQRState.qrData && (
                                <div className="mt-3">
                                    <label className="form-label">
                                        <FiMaximize2 className="me-2" />
                                        Données QR (URL complète)
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={secureQRState.qrData}
                                            readOnly
                                            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={handleCopyQRData}
                                        >
                                            <FiCopy className="me-2" />
                                            Copier
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="d-flex gap-2 mt-3">
                                <button
                                    type="button"
                                    className="btn btn-success flex-fill"
                                    onClick={handleRefresh}
                                    disabled={secureQRState.isLoading}
                                >
                                    <FiRefreshCw className="me-2" />
                                    Actualiser
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary flex-fill"
                                    onClick={handleBackToLogin}
                                >
                                    Retour à la connexion
                                </button>
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default SecureQRCode;

